/**
 * @module  public/js/utils/leadAllocationPause.js
 * @summary Shared browser-side helpers for user-level lead allocation pauses.
 *
 * @description
 * Normalizes the tenant-scoped `UserPrefs/{uid}.leadAllocationResumeDate`
 * value so profile, allocator admin, and Focus Mode all agree on when a user
 * is actively paused and when new leads should resume automatically.
 *
 * @author   TopSail AI
 * @version  1.0.0
 * @since    2026-03-06
 */

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format a date-like value as `yyyy-mm-dd` in the provided timezone.
 *
 * @param {Date|number|string} [dateLike=new Date()] - Value to normalize.
 * @param {string|null|undefined} [timeZone] - Preferred IANA timezone.
 * @returns {string} ISO day key or an empty string when the value is invalid.
 */
export function toISODateInTimeZone(dateLike = new Date(), timeZone) {
  const date =
    dateLike instanceof Date
      ? dateLike
      : typeof dateLike === 'number' || typeof dateLike === 'string'
        ? new Date(dateLike)
        : null;
  if (!date || Number.isNaN(date.getTime())) return '';

  const resolvedTimeZone =
    String(
      timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    ).trim() || undefined;

  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: resolvedTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const lookup = (type) =>
      parts.find((part) => part.type === type)?.value || '';
    const year = lookup('year');
    const month = lookup('month');
    const day = lookup('day');
    return year && month && day ? `${year}-${month}-${day}` : '';
  } catch (error) {
    console.warn('[leadAllocationPause] invalid timezone', {
      timeZone: resolvedTimeZone,
      error,
    });
    return toISODateInTimeZone(date);
  }
}

/**
 * Resolve whether a user's lead flow is actively paused.
 *
 * A pause is active only while the current seller-local day is strictly before
 * the stored resume date. When the resume date arrives, the pause is treated
 * as expired automatically without needing a cleanup job.
 *
 * @param {{ leadAllocationResumeDate?: string|null, tz?: string|null }|null|undefined} prefs - Stored user preferences.
 * @param {{ now?: Date|number|string }} [options={}] - Override clock for tests.
 * @returns {{ active: boolean, resumeDate: string, todayISO: string, timeZone: string }}
 */
export function resolveLeadAllocationPauseState(
  prefs = {},
  { now = new Date() } = {}
) {
  const timeZone =
    String(
      prefs?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    ).trim() || 'UTC';
  const todayISO = toISODateInTimeZone(now, timeZone);
  const rawResumeDate = String(prefs?.leadAllocationResumeDate || '').trim();
  const resumeDate = ISO_DATE_PATTERN.test(rawResumeDate) ? rawResumeDate : '';
  const active = Boolean(resumeDate && todayISO && todayISO < resumeDate);

  return {
    active,
    resumeDate: active ? resumeDate : '',
    todayISO,
    timeZone,
  };
}

/**
 * Format a stored resume date for user-facing copy.
 *
 * @param {string|null|undefined} resumeDate - `yyyy-mm-dd` resume date.
 * @param {string} [locale='en-US'] - Locale used for formatting.
 * @returns {string} Human-readable absolute date or an empty string.
 */
export function formatLeadAllocationResumeDate(resumeDate, locale = 'en-US') {
  const normalized = String(resumeDate || '').trim();
  if (!ISO_DATE_PATTERN.test(normalized)) return '';

  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(`${normalized}T12:00:00`));
  } catch (error) {
    console.warn('[leadAllocationPause] unable to format resume date', {
      resumeDate: normalized,
      error,
    });
    return normalized;
  }
}
